import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import {
  formatCurrency,
  formatVatRate,
  formatDate,
  isVatExempt,
  isIntraCommunity,
  resolveDocumentVatExemptArticle,
} from '../../../utils/vatCalculations';
import { QuoteTemplateProps, QuoteItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { LegalFooter } from '../shared/LegalFooter';
import { ItemsTable } from '../shared/ItemsTable';
import { publicLinksAPI, quotesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Modern - Design Your Brand (STARTER) - DEVIS
 * Design pastel orange (#fed7aa / bg-orange-200)
 * Layout deux colonnes, style épuré et moderne
 */
export const QuoteTemplateModern: React.FC<QuoteTemplateProps> = ({
  quoteData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { t } = useTranslation();
  const { company: companyStore, quoteColor: quoteColorStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const quoteColor = colorProp || quoteColorStore || template?.primaryColor || '#FED7AA';

  const clientCountryCode = (quoteData as any)?.Client?.countryCode;
  const isExempt = isVatExempt(company?.vatRegime) || isIntraCommunity(clientCountryCode, parseFloat(String(quoteData?.totalTVA)) || undefined);
  const vatExemptArticle = resolveDocumentVatExemptArticle(company?.vatRegime, clientCountryCode, parseFloat(String(quoteData?.totalTVA)) || undefined);

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">{t('templates.quote.noData')}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gradient-to-r from-teal-600 to-purple-600 text-white rounded-lg">
            {t('templates.common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Normaliser les items : spread pour garder tous les champs (lineType, unit, descriptionType, technicalDescription...)
  // + override des numériques (Prisma Decimal → number, totalHT → total)
  const items: QuoteItem[] = typeof quoteData.items === 'string'
    ? JSON.parse(quoteData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      })
    : (quoteData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      });

  const discountType = quoteData.discountType || 'none';
  const discountValue = parseFloat(String(quoteData.discountValue)) || 0;
  const discountAmount = parseFloat(String(quoteData.discountAmount)) || 0;

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: QuoteItem) => {
    const rate = item.taxPercent || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    const vatAmount = (item.total || 0) * (rate / 100);
    vatByRate[rate].vatAmount += vatAmount;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(quoteData.totalHT)) || parseFloat(String(quoteData.subtotal)) || 0;
  const tax = parseFloat(String(quoteData.totalTVA)) || parseFloat(String(quoteData.tax)) || 0;
  const total = parseFloat(String(quoteData.totalTTC)) || parseFloat(String(quoteData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!quoteData.id) {
      notify.error('ID du devis manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await quotesAPI.downloadPDF(quoteData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (quoteData as any).Client?.fullName || (quoteData as any).Client?.companyName || 'Client';
      const quoteNumber = (quoteData.quoteNumber || 'Devis').replace(/^DEVIS-/i, '');
      a.download = `Devis_${quoteNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      if (!quoteData.id) {
        throw new Error('ID du devis manquant');
      }

      // 1. Essayer de récupérer un lien existant
      try {
        const result = await publicLinksAPI.getQuoteLink(quoteData.id);
        if (result.success && result.data && result.data.length > 0) {
          const link = result.data[0];
          setPublicLink(`${window.location.origin}/q/${link.token}`);
          return;
        }
      } catch {
        // 404 ou erreur réseau — on continue pour créer un lien
      }

      // 2. Pas de lien existant, en créer un nouveau
      const createResult = await publicLinksAPI.createQuoteLink(quoteData.id);
      if (createResult.success && createResult.data && createResult.data.token) {
        setPublicLink(`${window.location.origin}/q/${createResult.data.token}`);
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!quoteData?.id) {
        throw new Error('ID du devis manquant');
      }

      setIsLoading(true);

      // Envoyer en JSON — le backend génère le PDF côté serveur
      await publicLinksAPI.sendQuoteEmail(quoteData.id, {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
      });

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGeneratePDF} // Même action que PDF pour devis
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        isLoading={isLoading}
        documentType="quote"
      />

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="quote"
        documentNumber={quoteData.quoteNumber}
        clientEmail={(quoteData as any).Client?.email || ''}
        clientName={(quoteData as any).Client?.fullName || (quoteData as any).Client?.companyName || ''}
        showPdfPreview={false}
        templateVariables={{
          quoteNumber: quoteData.quoteNumber || '',
          clientName: (quoteData as any).Client?.fullName || (quoteData as any).Client?.companyName || '',
          total: total.toFixed(2),
          publicLink: publicLink,
          validUntil: formatDate(quoteData.validUntil),
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        {/* Shadow wrapper - ombre portée pour mettre en avant le document */}
        <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full" id="quote-template">
          <div className="p-8 md:p-12 print:p-12">

            {/* ============================================ */}
            {/* HEADER - "Devis" (gauche) + "Design your BRAND" (droite) */}
            {/* ============================================ */}
            <div className="flex justify-between items-start mb-12 pb-8 border-b-2 border-gray-200">
              <div>
                <h1 className="text-3xl font-bold" style={{ color: quoteColor }}>{t('templates.quote.titleLower')}</h1>
                <div className="text-xs text-gray-600 mt-2">
                  N° <strong className="text-gray-800">{quoteData.quoteNumber?.replace(/^DEVIS-/i, '') || quoteData.quoteNumber}</strong>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {t('templates.quote.dateLabel')} <strong className="text-gray-800">{formatDate(quoteData.date)}</strong>
                </div>
              </div>

              <div className="text-right">
                {company?.logoUrl && (
                  <img
                    src={company.logoUrl}
                    alt={company.legalName || company.tradeName || 'Logo entreprise'}
                    className="h-16 md:h-20 max-w-[200px] object-contain ml-auto"
                  />
                )}
              </div>
            </div>

            {/* ============================================ */}
            {/* SECTION ADRESSES - Layout 2 colonnes */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">

              {/* COLONNE GAUCHE - Émetteur */}
              <div>
                <p className="text-sm font-bold text-gray-800 mb-1">
                  {company?.legalName || company?.tradeName}
                </p>

                {company?.addressLine1 && (
                  <p className="text-sm text-gray-600">
                    {company.addressLine1}
                  </p>
                )}

                {((company?.zipCode) ||
                  (company?.city)) && (
                  <p className="text-sm text-gray-600">
                    {company?.zipCode}{' '}
                    {company?.city}
                  </p>
                )}

                {company?.phone && (
                  <p className="text-sm text-gray-600 mt-2">
                    {t('templates.common.phoneShortSpaced')} {company.phone}
                  </p>
                )}

                {company?.email && (
                  <p className="text-sm text-gray-600">
                    {t('templates.common.emailShort')} {company.email}
                  </p>
                )}
              </div>

              {/* COLONNE DROITE - Client */}
              <div>
                {/* Utilise UNIQUEMENT la structure Prisma V2 */}
                {((quoteData as any).Client?.companyName || (quoteData as any).Client?.fullName) && (
                  <p className="text-sm font-bold text-gray-800 mb-1">
                    {(quoteData as any).Client?.companyName || (quoteData as any).Client?.fullName}
                  </p>
                )}

                {(quoteData as any).Client?.fullName && (quoteData as any).Client?.companyName && (
                  <p className="text-sm text-gray-600">{(quoteData as any).Client?.fullName}</p>
                )}

                {(quoteData as any).Client?.addressLine1 && (
                  <p className="text-sm text-gray-600">{(quoteData as any).Client?.addressLine1}</p>
                )}

                {((quoteData as any).Client?.zipCode || (quoteData as any).Client?.city) && (
                  <p className="text-sm text-gray-600">
                    {(quoteData as any).Client?.zipCode} {(quoteData as any).Client?.city}
                  </p>
                )}

                {(quoteData as any).Client?.email && (
                  <p className="text-sm text-gray-600 mt-2">
                    {t('templates.common.emailShort')} {(quoteData as any).Client?.email}
                  </p>
                )}

                {(quoteData as any).Client?.phone && (
                  <p className="text-sm text-gray-600">
                    {t('templates.common.phoneShortSpaced')} {(quoteData as any).Client?.phone}
                  </p>
                )}
              </div>
            </div>

            {/* ============================================ */}
            {/* DATES - Valable jusqu'au */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 pb-8 border-b border-gray-200">
              <div>
                <span className="text-sm text-gray-600">{t('templates.invoice.issueDateLabel')} </span>
                <span className="text-sm font-semibold text-gray-800">{formatDate(quoteData.date)}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('templates.quote.validUntilLabel')} </span>
                <span className="text-sm font-semibold text-gray-800">{formatDate(quoteData.validUntil)}</span>
              </div>
            </div>

            {/* ============================================ */}
            {/* TITRE DU DOCUMENT */}
            {/* ============================================ */}
            {quoteData.title && (
              <div
                className="mb-8 text-2xl font-bold pb-2 border-b-2 text-gray-800"
                style={{
                  borderImage: `linear-gradient(135deg, ${quoteColor} 0%, #14b8a6 100%) 1`
                }}
              >
                {quoteData.title}
              </div>
            )}

            {/* TABLEAU DES ITEMS — délégué à ItemsTable (sections, remises, descriptions BTP) */}
            <ItemsTable
              items={items}
              tableStyle="minimal"
              accentColor={quoteColor}
              showVat={!isExempt}
            />

            {/* ============================================ */}
            {/* TOTAUX - Alignés à droite avec ventilation TVA */}
            {/* ============================================ */}
            <div className="flex flex-col items-end mb-12">
              <div className="w-full md:w-1/2 space-y-3">

                {/* Sous-total HT */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">{t('templates.common.subtotalHT')}</span>
                  <span className="text-gray-800 font-semibold">{formatCurrency(subtotal)}</span>
                </div>

                {/* Remise */}
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600 font-medium">
                      {t('templates.common.discount')}
                      {discountType === 'percentage' && discountValue
                        ? ` (${discountValue}%)`
                        : ''}
                    </span>
                    <span className="text-red-600 font-semibold">- {formatCurrency(discountAmount)}</span>
                  </div>
                )}

                {/* Ventilation TVA - uniquement si plusieurs taux */}
                {!isExempt && vatBreakdown.length > 1 && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">{t('templates.common.vatBreakdownTitle')}</h4>
                    {vatBreakdown.map((vat, index) => (
                      <div key={index} className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">
                          TVA {formatVatRate(vat.rate)} sur {formatCurrency(vat.baseHT)}
                        </span>
                        <span className="text-gray-800 font-medium">{formatCurrency(vat.vatAmount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                      <span className="text-gray-600 font-medium">{t('templates.common.totalVAT')}</span>
                      <span className="text-gray-800 font-semibold">{formatCurrency(tax)}</span>
                    </div>
                  </div>
                )}

                {/* TVA simple si un seul taux */}
                {!isExempt && vatBreakdown.length <= 1 && tax > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600 font-medium">
                      TVA {vatBreakdown.length === 1 ? formatVatRate(vatBreakdown[0].rate) : ''}
                    </span>
                    <span className="text-gray-800 font-semibold">{formatCurrency(tax)}</span>
                  </div>
                )}

                {/* Total TTC */}
                <div
                  className="flex justify-between items-center py-3 px-4 rounded-lg"
                  style={{ backgroundColor: `${quoteColor}15` }}
                >
                  <span className="text-lg font-bold text-gray-800">{t('templates.common.totalTTCUpper')}</span>
                  <span className="text-xl md:text-2xl font-bold" style={{ color: quoteColor }}>
                    {formatCurrency(total)}
                  </span>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* FOOTER LÉGAL */}
            {/* ============================================ */}
            <LegalFooter company={company} notes={quoteData.termsConditions} vatExemptArticle={vatExemptArticle} />

          </div>
        </div>
      </div>
    </div>
  );
};
